import { InertiaProps, SubSeccion } from "@/types/app";
import { PattyAlert, capturarError, delay, getImageUrl } from "@/utils/global";
import { usePage } from "@inertiajs/react";
import * as Icon from "@phosphor-icons/react/dist/ssr";
import axios from "axios";
import { useState } from "react";
import { FaFacebookF } from "react-icons/fa";
import { FaTiktok } from "react-icons/fa6";
import { IoCallOutline, IoLocationSharp, IoLogoWhatsapp, IoTimeOutline } from "react-icons/io5";
import { z } from "zod";
import CakeLoader from "../loaders/cakeLoader";

const Footer = () => {
    const { navbar, footer } = usePage<InertiaProps>().props;

    const [newsletterEmail, setNewsLetterEmail] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const saveNewsletter = async () => {
        const schema = z.object({
            email: z
                .string({ required_error: "E-mail requerido" })
                .email({ message: "Formato de email incorrecto" }),
        });
        try {
            const data = schema.parse({
                email: newsletterEmail,
            });
            setIsLoading(true);
            await delay(2000);

            const url = `${window.origin}/api/newsletter/add`;
            const response = await axios.post(url, data);
            if (response.status === 200) {
                setIsLoading(false);
                setNewsLetterEmail("");
                PattyAlert({
                    icon: "success",
                    title: "Exito",
                    message:
                        "Email añadido correctamente a la lista de newsletter.",
                });
            }
        } catch (err) {
            setIsLoading(false);
            console.log(err);
            capturarError(err);
        }
    };

    const limitSubSeccion = (subSeccion: SubSeccion[]) => {
        //ordenar de forma aleatoria y limitar a 3
        return subSeccion.sort(() => Math.random() - Math.random()).slice(0, 3);
    };

    return (
        <footer id="footer" className="bg-gray-50 relative"
            style={{
                clipPath: 'polygon(0% 20px, 10% 10px, 20% 30px, 30% 5px, 40% 25px, 50% 8px, 60% 28px, 70% 12px, 80% 32px, 90% 15px, 100% 35px, 100% 100%, 0% 100%)'
            }}>
            {/* Fondo con patrón de pasteles difuminado */}
            <div
                className="absolute inset-0 opacity-35"
                style={{
                    backgroundImage: `url(${getImageUrl("/images/patrones/fondo%20test.png")})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            ></div>

            {isLoading && <CakeLoader />}

            {/* Main Footer */}
            <div className="container mx-auto px-6 pt-16 pb-16 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

                    {/* Brand & Contact - Takes 2 columns */}
                    <div className="lg:col-span-2 space-y-8">
                        <div>
                            <a href="/" className="inline-block">
                                <div className="font-[StoneStick] heading4 text-[#790546] text-4xl mb-4">
                                    Patty Cariño
                                </div>
                                <p className="text-gray-600 text-lg max-w-md leading-relaxed">
                                    Las mejores tortas y pasteles caseros de La Florida.
                                    Endulzamos tus momentos especiales con amor y tradición familiar.
                                </p>
                            </a>
                        </div>

                        {/* Contact Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-gradient-to-br from-white to-pink-50 p-5 rounded-xl shadow-md border border-pink-100 hover:shadow-lg transition-shadow">
                                <h4 className="text-lg font-bold text-[#790546] mb-4 flex items-center gap-2">
                                    <IoCallOutline className="text-xl" />
                                    Cafetería
                                </h4>
                                <div className="space-y-2">
                                    <a href="tel:+56222624228" className="flex items-center gap-2 text-gray-700 hover:text-[#790546] transition-colors text-sm font-medium group">
                                        <span className="text-lg group-hover:scale-110 transition-transform">📞</span>
                                        <span>222624228</span>
                                    </a>
                                    <a href="tel:+56985326336" className="flex items-center gap-2 text-gray-700 hover:text-[#790546] transition-colors text-sm font-medium group">
                                        <span className="text-lg group-hover:scale-110 transition-transform">📱</span>
                                        <span>985326336</span>
                                    </a>
                                </div>
                                <div className="mt-3 text-xs text-gray-500 flex items-center gap-1">
                                    <span>☕</span>
                                    <span>Tortas express, trozo de torta, pastelería y más</span>
                                </div>
                            </div>

                            <div className="bg-gradient-to-br from-white to-purple-50 p-5 rounded-xl shadow-md border border-purple-100 hover:shadow-lg transition-shadow">
                                <h4 className="text-lg font-bold text-[#790546] mb-4 flex items-center gap-2">
                                    <IoCallOutline className="text-xl" />
                                    Pedidos
                                </h4>
                                <div className="space-y-2">
                                    <a href="tel:+56222624228" className="flex items-center gap-2 text-gray-700 hover:text-[#790546] transition-colors text-sm font-medium group">
                                        <span className="text-lg group-hover:scale-110 transition-transform">📞</span>
                                        <span>222624228</span>
                                    </a>
                                    <a href="tel:+56224192574" className="flex items-center gap-2 text-gray-700 hover:text-[#790546] transition-colors text-sm font-medium group">
                                        <span className="text-lg group-hover:scale-110 transition-transform">📞</span>
                                        <span>224192574</span>
                                    </a>
                                </div>
                                <div className="mt-3 text-xs text-gray-500 flex items-center gap-1">
                                    <span>🎂</span>
                                    <span>Pedidos personalizados</span>
                                </div>
                            </div>

                            <div className="bg-gradient-to-br from-white to-rose-50 p-5 rounded-xl shadow-md border border-rose-100 hover:shadow-lg transition-shadow">
                                <h4 className="text-lg font-bold text-[#790546] mb-4 flex items-center gap-2">
                                    <IoCallOutline className="text-xl" />
                                    Novios
                                </h4>
                                <div className="space-y-2">
                                    <a href="https://wa.me/56996935307" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-700 hover:text-[#790546] transition-colors text-sm font-medium group">
                                        <IoLogoWhatsapp className="text-xl text-green-500 group-hover:scale-110 transition-transform" />
                                        <span>996935307</span>
                                    </a>
                                </div>
                                <div className="mt-3 text-xs text-gray-500 flex items-center gap-1">
                                    <span>💍</span>
                                    <span>Eventos especiales</span>
                                </div>
                            </div>

                            <div className="bg-gradient-to-br from-white to-amber-50 p-5 rounded-xl shadow-md border border-amber-100 hover:shadow-lg transition-shadow">
                                <h4 className="text-lg font-bold text-[#790546] mb-4 flex items-center gap-2">
                                    <IoCallOutline className="text-xl" />
                                    Diseños
                                </h4>
                                <div className="space-y-2">
                                    <a href="https://wa.me/56961405100" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-700 hover:text-[#790546] transition-colors text-sm font-medium group">
                                        <IoLogoWhatsapp className="text-xl text-green-500 group-hover:scale-110 transition-transform" />
                                        <span>961405100</span>
                                    </a>
                                </div>
                                <div className="mt-3 text-xs text-gray-500 flex items-center gap-1">
                                    <span>🎨</span>
                                    <span>Creaciones personalizadas</span>
                                </div>
                            </div>
                        </div>

                        {/* Location & Hours */}
                        <div className="flex flex-col sm:flex-row gap-6">
                            <a href="/ubicacion" className="flex items-center gap-3 text-gray-700 hover:text-[#790546] transition-colors group bg-white p-4 rounded-lg shadow-sm">
                                <IoLocationSharp className="text-[#790546] text-xl group-hover:scale-110 transition-transform" />
                                <div>
                                    <p className="font-semibold">Visítanos</p>
                                    <p className="text-sm text-gray-500">La Florida, Santiago</p>
                                </div>
                            </a>
                            <a href="/ubicacion" className="flex items-center gap-3 text-gray-700 hover:text-[#790546] transition-colors group bg-white p-4 rounded-lg shadow-sm">
                                <IoTimeOutline className="text-[#790546] text-xl group-hover:scale-110 transition-transform" />
                                <div>
                                    <p className="font-semibold">Horarios</p>
                                    <p className="text-sm text-gray-500 hover:underline">Ver horarios</p>
                                </div>
                            </a>
                        </div>
                    </div>

                    {/* Navigation Links */}
                    <div className="space-y-6">
                        <h3 className="text-2xl font-bold text-[#790546]">Navegación</h3>
                        <div className="grid grid-cols-1 gap-8">
                            {footer.slice(0, 2).map((item) => (
                                <div key={item.titulo.id}>
                                    <h4 className="text-lg font-semibold text-gray-800 mb-4">
                                        {item.titulo.nombre}
                                    </h4>
                                    <ul className="space-y-3">
                                        {item.subTitulos.map((submenu) => (
                                            <li key={submenu.id}>
                                                <a
                                                    href={submenu.link}
                                                    className="text-gray-600 hover:text-[#790546] transition-colors text-sm font-medium"
                                                >
                                                    {submenu.nombre}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Newsletter & Social */}
                    <div className="space-y-8">
                        <h3 className="text-2xl font-bold text-[#790546]">Mantente Conectado</h3>

                        {/* Newsletter */}
                        <div className="bg-white p-6 rounded-xl shadow-sm space-y-4">
                            <p className="text-gray-600 text-sm leading-relaxed">
                                Recibe ofertas exclusivas y novedades directamente en tu email
                            </p>
                            <div className="space-y-3">
                                <input
                                    type="email"
                                    placeholder="Tu email aquí..."
                                    value={newsletterEmail}
                                    onChange={(e) => setNewsLetterEmail(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-[#790546]/20 focus:border-[#790546] outline-none transition-all"
                                />
                                <button
                                    onClick={saveNewsletter}
                                    className="w-full px-6 py-3 bg-[#790546] text-white rounded-lg hover:bg-[#6a0440] transition-colors font-semibold flex items-center justify-center gap-2"
                                >
                                    Suscribirse
                                    <Icon.ArrowRight size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Social Media */}
                        <div className="space-y-4">
                            <h4 className="text-lg font-semibold text-[#790546]">Síguenos</h4>
                            <div className="flex gap-4">
                                <a
                                    href="https://www.facebook.com/TortasPattyCarino/?locale=es_LA&checkpoint_src=any"
                                    target="_blank"
                                    className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors hover:scale-110 transform shadow-lg"
                                >
                                    <FaFacebookF size={18} />
                                </a>
                                <a
                                    href="https://www.instagram.com/tortaspattycarino/"
                                    target="_blank"
                                    className="w-12 h-12 bg-gradient-to-r from-[#E4405F] to-[#C13584] text-white rounded-full flex items-center justify-center hover:from-[#D63031] hover:to-[#A020F0] transition-colors hover:scale-110 transform shadow-lg"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                    </svg>
                                </a>
                                <a
                                    href="https://www.tiktok.com/@pattypasteleria"
                                    target="_blank"
                                    className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors hover:scale-110 transform shadow-lg"
                                >
                                    <FaTiktok size={18} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Bottom */}
            <div className="border-t border-gray-300 bg-gray-100 relative z-10">
                <div className="container mx-auto px-6 py-6">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-6">

                        {/* Copyright */}
                        <div className="text-center lg:text-left">
                            <p className="text-gray-600 text-sm font-medium">
                                &copy; {new Date().getFullYear()} Patty Cariño. Todos los derechos reservados.
                            </p>
                        </div>

                        {/* Payment Methods */}
                        <div className="flex flex-col items-center lg:items-end gap-3">
                            <p className="text-gray-600 text-sm font-semibold">Métodos de Pago Seguros</p>
                            <div className="flex items-center gap-4 flex-wrap justify-center">
                                <img src="https://www.flow.cl/images/header/logo-flow.svg" alt="Flow" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                                <img src="https://www.flow.cl/img/logo_check.png" alt="Check" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                                <img src="https://www.flow.cl/img/logo_mach.png" alt="Mach" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                                <img src="https://www.flow.cl/img/logo_onepay.png" alt="OnePay" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                                <img src="https://www.flow.cl/img/logo_transfer.png?v=1" alt="Transfer" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                                <img src="https://www.flow.cl/img/logo_webpay.png" alt="WebPay" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Developer Credit - White Background */}
            <div className="bg-white border-t border-gray-100 relative z-10">
                <div className="container mx-auto px-6 py-4">
                    <div className="text-center">
                        <p className="text-gray-500 text-xs">
                            Desarrollado con ❤️ por <a href="https://refreshchile.cl" className="hover:text-[#790546] transition-colors font-medium">Refresh Chile</a>
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
